import {
    buildFlowGrid,
    isPointInFlowShape,
    quantizeAngleRad,
    sampleAngle,
    type FlowFieldModeOptions,
    type FlowGrid,
    type Noise01,
} from "./flowField";

const DEFAULT_VECTOR_LEN = 4;
const DEFAULT_STRIDE = 3;

/**
 * Short line segments showing local flow direction (inside artboard), for debug overlay.
 */
export function computeFlowFieldDebugSegments(
    width: number,
    height: number,
    marginFactor: number,
    cellSize: number,
    noiseScale: number,
    noiseAngle: Noise01,
    noiseStep: Noise01 | null,
    stepNoiseScale: number,
    quantizeRad: number,
    options?: {
        vectorLen?: number;
        stride?: number;
        mode?: FlowFieldModeOptions;
        /** Skip a second `buildFlowGrid` when the caller already has the grid. */
        grid?: FlowGrid;
    },
): { x1: number; y1: number; x2: number; y2: number }[] {
    const vectorLen = options?.vectorLen ?? DEFAULT_VECTOR_LEN;
    const stride = options?.stride ?? DEFAULT_STRIDE;
    const grid =
        options?.grid ??
        buildFlowGrid(
            width,
            height,
            marginFactor,
            cellSize,
            noiseScale,
            noiseAngle,
            noiseStep,
            stepNoiseScale,
            options?.mode,
        );
    const segs: { x1: number; y1: number; x2: number; y2: number }[] = [];
    const nx = grid.numCols + 1;
    const ny = grid.numRows + 1;
    for (let r = 0; r < ny; r += stride) {
        for (let c = 0; c < nx; c += stride) {
            const x = grid.left + c * grid.cellSize;
            const y = grid.top + r * grid.cellSize;
            if (x < 0 || y < 0 || x > width || y > height) continue;
            if (!isPointInFlowShape(grid, x, y)) continue;
            let a = sampleAngle(grid, x, y);
            if (quantizeRad > 0) a = quantizeAngleRad(a, quantizeRad);
            segs.push({
                x1: x,
                y1: y,
                x2: x + vectorLen * Math.cos(a),
                y2: y + vectorLen * Math.sin(a),
            });
        }
    }
    return segs;
}
